import { Box } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import EnhancedTable from 'src/components/shared/EnhancedTable/EnhancedTable'
import { InputField } from 'src/components/shared/ui/form/fields'
import AutoCompleteField from 'src/components/shared/ui/form/fields/AutoCompleteField'
import RenderDocumentIcon from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/RenderDocumentIcon'
import { documentTableStyles as styles } from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/tables/DocumentoTablesStyles'
import { parseTextFileName } from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/types'
import RemoveInfoButton from 'src/components/views/EscritosLexnet/components/shared/components/RemoveInfoButton'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { TipoAdjunto, TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { useGetSubtipoDocumento } from 'src/hooks/fetch/useCatalogoGeneralFilters'
import { Orden } from 'src/interfaces/Catalog'
import { DocumentoAnexoType } from 'src/interfaces/EscritosLegales'

function DocumentosAnexosTable(): JSX.Element {
  const [fieldInfo] = useField(FormField.DOCUMENTOS_ANEXOS)
  const [documentosAnexosArchivo] = useField(FormField.DOCUMENTOS_ANEXOS_ARCHIVO)
  const { setFieldValue } = useFormikContext()
  const { t } = useTranslation()
  const groupByFn = option => option.tipoDocumento.descripcion
  const { tipoMensaje, isMonitorio } = useTipoMensajeContext()
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )

  const items = useGetSubtipoDocumento(FormField.DOCUMENTOS_ANEXOS, [
    {
      tipoMensaje: isMonitorio ? TipoMensaje.MONITORIO_CATALOGO : tipoMensaje,
      orden: orden.value ? orden.value.orden : null,
      tipoAdjunto: TipoAdjunto.ANEXO
    }
  ])

  const deleteRow = (_item: DocumentoAnexoType, index: number) => {
    if (index !== -1) {
      const newData = [...fieldInfo.value.slice(0, index), ...fieldInfo.value.slice(index + 1)]
      setFieldValue(FormField.DOCUMENTOS_ANEXOS, newData)
      const newData2 = [
        ...documentosAnexosArchivo.value.slice(0, index),
        ...documentosAnexosArchivo.value.slice(index + 1)
      ]
      setFieldValue(FormField.DOCUMENTOS_ANEXOS_ARCHIVO, newData2)
    }
  }

  const columns = [
    {
      Header: 'Anexos',
      accessor: 'archivo',
      Cell: ({ value }) => {
        return (
          <>
            <Box sx={styles.documentIcon}>
              <RenderDocumentIcon file={value} multiple />
            </Box>
            {parseTextFileName(value)}
          </>
        )
      },
      width: 200
    },
    {
      Header: t('form.field.catalogacion'),
      accessor: (_item: DocumentoAnexoType, index: number) => (
        <Box sx={styles.container}>
          <Box sx={styles.autocomplete}>
            <AutoCompleteField
              id={`${FormField.DOCUMENTOS_ANEXOS}.${index}.subtipo`}
              items={items}
              label={t('form.field.catalogacion')}
              groupBy={groupByFn}
              groupName="descripcion"
              itemName="descripcion"
              required
            />
          </Box>
        </Box>
      ),
      width: 350
    },
    {
      Header: t('form.field.descripcion'),
      accessor: (_item: DocumentoAnexoType, index: number) => (
        <Box sx={styles.container}>
          <Box sx={styles.textarea}>
            <InputField
              id={`${FormField.DOCUMENTOS_ANEXOS}.${index}.descripcion`}
              label={t('form.field.descripcion')}
              maxLength={100}
            />
          </Box>
        </Box>
      ),
      width: 350
    },
    {
      Header: ' ',
      accessor: (item: DocumentoAnexoType, index: number) => (
        <Box className="cellActions">
          <RemoveInfoButton
            clear={() => deleteRow(item, index)}
            firstDialogMessage={t('dialog.message.remove_documento_anexo')}
          />
        </Box>
      ),
      width: 55
    }
  ]

  return (
    <div id={FormField.DOCUMENTOS_ANEXOS}>
      <EnhancedTable
        columns={columns}
        data={fieldInfo.value && Object.keys(fieldInfo.value).length > 0 ? fieldInfo.value : []}
        toolbar={false}
        variation="form"
        isDocumentSection
        noRegistersMessage={
          isMonitorio
            ? t('components.enhanced_table.no_registers.documentos_anexos_monitorio')
            : t('components.enhanced_table.no_registers.documentos_anexos')
        }
        footer={false}
        pSize={50}
      />
    </div>
  )
}

export default DocumentosAnexosTable
